import isEqual from 'lodash-es/isEqual'
import union from 'lodash-es/union'
import getSafeLocalStorage from './safe_local_storage'
import { emit } from 'shared/javascripts/util/events'
import { postForm } from 'shared/javascripts/util/request'

const CURRENT_SESSION_VERSION = 3

// GA4 ends a session after 30 minutes without activity.
export const ANALYTICS_SESSION_TIMEOUT = 30 * 60 * 1000

export class Session {
  private store: Store
  private bookmarkCallbacks: Array<(bookmarksChanged: boolean) => void>
  private profileLoad: Promise<unknown> | null

  constructor() {
    this.store = new Store('studyflix')
    this.bookmarkCallbacks = []
    this.profileLoad = null
    this._migrate()
  }

  deferSyncUntilLoaded(profileLoad: Promise<unknown>): void {
    this.profileLoad = profileLoad
  }

  onBookmarkChange(callback: (bookmarksChanged: boolean) => void): void {
    this.bookmarkCallbacks.push(callback)
  }

  get sessionVersion(): number {
    const version = Number(this.store.read('sessionVersion'))
    return Number.isNaN(version) ? 0 : version
  }

  set sessionVersion(sessionVersion: number | null) {
    this.store.write('sessionVersion', sessionVersion)
  }

  get userId(): number | null {
    return this.store.read('userId') as number | null
  }

  set userId(userId: number | null) {
    this.store.write('userId', userId)
  }

  get userName(): string | null {
    return this.store.read('userName') as string | null
  }

  set userName(userName: string | null) {
    this.store.write('userName', userName)
  }

  get signedIn(): boolean | null {
    return this.store.read('signedIn') as boolean | null
  }

  set signedIn(signedIn: boolean | null) {
    this.store.write('signedIn', signedIn)
  }

  get academyPartnerName(): string | null {
    return this.store.read('academyPartnerName') as string | null
  }

  set academyPartnerName(academyPartnerName: string | null) {
    this.store.write('academyPartnerName', academyPartnerName)
  }

  get academyPartnerId(): number | null {
    return this.store.read('academyPartnerId') as number | null
  }

  set academyPartnerId(academyPartnerId: number | null) {
    this.store.write('academyPartnerId', academyPartnerId)
  }

  /** The user's role within their academy partner: admin, trainer or learner. */
  get academyFunction(): string | null {
    return this.store.read('academyFunction') as string | null
  }

  set academyFunction(academyFunction: string | null) {
    this.store.write('academyFunction', academyFunction)
  }

  get emailConfirmed(): boolean | null {
    return this.store.read('emailConfirmed') as boolean | null
  }

  set emailConfirmed(emailConfirmed: boolean | null) {
    this.store.write('emailConfirmed', emailConfirmed)
  }

  get role(): string | null {
    return this.store.read('role') as string | null
  }

  set role(role: string | null) {
    this.store.write('role', role)
  }

  get remindAgain(): number | null {
    return this.store.read('remindAgain') as number | null
  }

  set remindAgain(value: number | null) {
    this.store.write('remindAgain', value)
  }

  get lastCareerReminder(): string | null {
    return this.store.read('lastCareerReminder') as string | null
  }

  set lastCareerReminder(value: string | null) {
    this.store.write('lastCareerReminder', value)
  }

  get educationLevelOverlaySeen(): boolean | null {
    return this.store.read('educationLevelOverlaySeen') as boolean | null
  }

  set educationLevelOverlaySeen(value: boolean | null) {
    this.store.write('educationLevelOverlaySeen', value)
  }

  get appRatingOverlaySeen(): boolean | null {
    return this.store.read('appRatingOverlaySeen') as boolean | null
  }

  set appRatingOverlaySeen(value: boolean | null) {
    this.store.write('appRatingOverlaySeen', value)
  }

  get nativeAppAdvertisementSeen(): boolean | null {
    return this.store.read('nativeAppAdvertisementSeen') as boolean | null
  }

  set nativeAppAdvertisementSeen(value: boolean | null) {
    this.store.write('nativeAppAdvertisementSeen', value)
  }

  get preAdRoleChosen(): boolean | null {
    return this.store.read('preAdRoleChosen') as boolean | null
  }

  set preAdRoleChosen(value: boolean | null) {
    this.store.write('preAdRoleChosen', value)
  }

  getLastAdId(adUnit: string): string | null {
    return this.store.read(this.lastAdIdKey(adUnit)) as string | null
  }

  setLastAdId(adUnit: string, value: string): void {
    this.store.write(this.lastAdIdKey(adUnit), value)
  }

  lastAdIdKey(adUnit: string): string {
    return `lastAdId:${adUnit}`
  }

  get frequencyCappingDate(): string | null {
    return this.store.read('frequencyCappingDate') as string | null
  }

  set frequencyCappingDate(value: string | null) {
    this.store.write('frequencyCappingDate', value)
  }

  get sessionIdentifier(): string {
    const stored = this.store.read('sessionIdentifier') as string | null
    if (stored) return stored
    const newIdentifier = crypto.randomUUID()
    this.store.write('sessionIdentifier', newIdentifier)
    return newIdentifier
  }

  // GA4 groups events into sessions by a `session_id` that gtag maintains in its `_ga_<container>`
  // cookie. When an ad blocker keeps gtag from loading, that cookie never exists, so we mint an
  // equivalent id ourselves: a unix timestamp that stays stable until the GA4 inactivity window passes.
  get analyticsSessionId(): string {
    const now = Date.now()
    const storedId = this.store.read('analyticsSessionId') as string | null
    const lastActivity = this.store.read('analyticsSessionLastActivityAt') as number | null
    const expired = !lastActivity || now - lastActivity > ANALYTICS_SESSION_TIMEOUT

    const sessionId = storedId && !expired ? storedId : String(Math.floor(now / 1000))
    this.store.write('analyticsSessionId', sessionId)
    this.store.write('analyticsSessionLastActivityAt', now)
    return sessionId
  }

  get seenVideoIds(): number[] {
    return (this.store.read('seenVideoIds') as number[] | null) || []
  }

  get seenVideos(): number {
    const seenVideosIds = this.seenVideoIds
    return seenVideosIds.length
  }

  set seenVideoIds(videoIds: number[]) {
    if (!isEqual(this.seenVideoIds, videoIds)) {
      this.store.write('seenVideoIds', videoIds)
    }
  }

  setVideoSeen(videoId: number): void {
    const seenVideoIds = this.seenVideoIds
    if (seenVideoIds[seenVideoIds.length - 1] === videoId) return

    this.seenVideoIds = union(this._removeFromArray(seenVideoIds, videoId), [videoId])
    emit('app.session.videoSeen', { videoId })
    this.syncAfterProfileLoad()
  }

  reduceRemindCounter(): void {
    const counter = this.remindAgain ?? 0
    this.remindAgain = counter - 1
  }

  get bookmarkedVideoIds(): number[] {
    return (this.store.read('bookmarkedVideoIds') as number[] | null) || []
  }

  set bookmarkedVideoIds(videoIds: number[]) {
    if (!isEqual(this.bookmarkedVideoIds, videoIds)) {
      this.store.write('bookmarkedVideoIds', videoIds)
      this._runBookmarkCallbacks(true)
    }
  }

  addBookmarkedVideo(videoId: number): void {
    this.bookmarkedVideoIds = union(this.bookmarkedVideoIds, [videoId])
    emit('app.session.videoBookmarked', { videoId })
    this.sync()
  }

  removeBookmarkedVideo(videoId: number): void {
    this.bookmarkedVideoIds = this._removeFromArray(this.bookmarkedVideoIds, videoId)
    emit('app.session.videoUnmarked', { videoId })
    this.sync()
  }

  // study infos

  set educationLevel(educationLevel: string | null) {
    if (this.educationLevel !== educationLevel) {
      this.store.write('educationLevel', educationLevel)
    }
  }

  get educationLevel(): string | null {
    return this.store.read('educationLevel') as string | null
  }

  get subjectAreaIdAndKey(): string | null {
    return this.store.read('subjectAreaIdAndKey') as string | null
  }

  set subjectAreaIdAndKey(subjectAreaIdAndKey: string | null) {
    if (this.subjectAreaIdAndKey !== subjectAreaIdAndKey) {
      this.store.write('subjectAreaIdAndKey', subjectAreaIdAndKey)
    }
  }

  get subjectAreaId(): string | undefined {
    // ID can only be known from a filled-out profile / sign-up form.
    return this._getId(this.subjectAreaIdAndKey)
  }

  get subjectAreaKey(): string | null | undefined {
    const keyFromProfile = this._getKey(this.subjectAreaIdAndKey)
    const keyFromCurrentPage = this.subjectAreaCurrentKey

    return keyFromProfile || keyFromCurrentPage
  }

  get subjectAreaCurrentKey(): string | null {
    // Note that this *ignores* any data from the user profile.
    // You almost always want to read subjectAreaKey.
    return this.store.read('subjectAreaCurrentKey') as string | null
  }

  set subjectAreaCurrentKey(key: string | null) {
    if (this.subjectAreaCurrentKey !== key) {
      this.store.write('subjectAreaCurrentKey', key)
    }
  }

  get studyCourseIdAndKey(): string | undefined {
    return this.store.read('studyCourseIdAndKey') as string | undefined
  }

  set studyCourseIdAndKey(studyCourseIdAndKey: string | undefined) {
    if (this.studyCourseIdAndKey !== studyCourseIdAndKey) {
      this.store.write('studyCourseIdAndKey', studyCourseIdAndKey)
    }
  }

  get studyCourseId(): string | undefined {
    return this._getId(this.studyCourseIdAndKey)
  }

  get studyCourseKey(): string | undefined {
    return this._getKey(this.studyCourseIdAndKey)
  }

  get universityIdAndKey(): string | undefined {
    return this.store.read('universityIdAndKey') as string | undefined
  }

  set universityIdAndKey(universityIdAndKey: string | undefined) {
    if (this.universityIdAndKey !== universityIdAndKey) {
      this.store.write('universityIdAndKey', universityIdAndKey)
    }
  }

  get universityId(): string | undefined {
    return this._getId(this.universityIdAndKey)
  }

  get universityKey(): string | undefined {
    return this._getKey(this.universityIdAndKey)
  }

  get studyStart(): string | null {
    return this.store.read('studyStart') as string | null
  }

  set studyStart(studyStart: string | null) {
    if (this.studyStart !== studyStart) {
      this.store.write('studyStart', studyStart)
    }
  }

  get numberOfSemesters(): number | undefined {
    let result: number | undefined

    if (this.studyStart) {
      result = this._calculateNumberOfSemesters(this.studyStart, new Date())
    }

    return result
  }

  // school infos

  get schoolRegion(): string | null {
    return this.store.read('schoolRegion') as string | null
  }

  set schoolRegion(schoolRegion: string | null) {
    if (this.schoolRegion !== schoolRegion) {
      this.store.write('schoolRegion', schoolRegion)
    }
  }

  get typeOfSchool(): string {
    let typeOfSchool = ''
    const name = this.schoolName
    if (name) {
      typeOfSchool = name.match(/(?:de|at|ch)-(.*)/)?.[1] ?? ''
    }
    return typeOfSchool
  }

  get schoolName(): string | null {
    return this.store.read('schoolName') as string | null
  }

  set schoolName(schoolName: string | null) {
    if (this.schoolName !== schoolName) {
      this.store.write('schoolName', schoolName)
    }
  }

  get schoolGrade(): string | null {
    return this.store.read('schoolGrade') as string | null
  }

  set schoolGrade(schoolGrade: string | null) {
    if (this.schoolGrade !== schoolGrade) {
      this.store.write('schoolGrade', schoolGrade)
    }
  }

  get inLowerSchool(): boolean {
    return !!this.schoolGrade && parseInt(this.schoolGrade) <= 10
  }

  get inUpperSchool(): boolean {
    return !!this.schoolGrade && parseInt(this.schoolGrade) > 10
  }

  // place infos

  get schoolZipcode(): string | null {
    return this.store.read('schoolZipcode') as string | null
  }

  set schoolZipcode(schoolZipcode: string | null) {
    if (this.schoolZipcode !== schoolZipcode) {
      this.store.write('schoolZipcode', schoolZipcode)
    }
  }

  // personal infos

  get gender(): string | null {
    return this.store.read('gender') as string | null
  }

  set gender(gender: string | null) {
    if (this.gender !== gender) {
      this.store.write('gender', gender)
    }
  }

  // registration step

  get lastCompletedStep(): string | null {
    return this.store.read('lastCompletedStep') as string | null
  }

  set lastCompletedStep(lastCompletedStep: string | null) {
    if (this.lastCompletedStep !== lastCompletedStep) {
      this.store.write('lastCompletedStep', lastCompletedStep)
    }
  }

  clear(): void {
    this.store.clear()
  }

  sync(): void {
    if (this.signedIn) {
      void postForm('/user/synchronize_profile', {
        'synchronisation[seen_video_ids][]': this.seenVideoIds,
        'synchronisation[bookmarked_video_ids][]': this.bookmarkedVideoIds,
      })
    }
  }

  private syncAfterProfileLoad(): void {
    if (this.profileLoad) {
      void this.profileLoad.then(() => this.sync())
    } else {
      this.sync()
    }
  }

  private _runBookmarkCallbacks(bookmarksChanged: boolean): void {
    for (const callback of this.bookmarkCallbacks) {
      callback(bookmarksChanged)
    }
  }

  private _removeFromArray(array: number[], element: number): number[] {
    const tempArray = array
    const index = tempArray.indexOf(element)
    if (index > -1) {
      tempArray.splice(index, 1)
    }
    return tempArray
  }

  private _getId(idAndKey: string | null | undefined): string | undefined {
    return this._splitIdAndKey(idAndKey ?? undefined)[0]
  }

  private _getKey(idAndKey: string | null | undefined): string | undefined {
    return this._splitIdAndKey(idAndKey ?? undefined)[1]
  }

  private _splitIdAndKey(idAndKey: string | undefined): [string | undefined, string | undefined] {
    const [id, key] = idAndKey ? idAndKey.split('/') : []
    return [id, key]
  }

  private _calculateNumberOfSemesters(studyStart: string, currentDate: Date): number {
    let count = 0
    const currentYear = currentDate.getFullYear()
    const currentMonth = currentDate.getMonth()
    const yearAndSemester = studyStart.match(/(SoSe|WiSe|vor)\s(\d{4})/)!
    const semester = yearAndSemester[1]
    const year = parseInt(yearAndSemester[2])

    if (studyStart === 'vor 2005') {
      count = 10
    } else if (year !== currentYear) {
      count += semester === 'SoSe' ? 2 : 1
      count += (currentYear - year - 1) * 2
      if (currentMonth > 3) {
        count += currentMonth < 10 ? 1 : 2
      }
    } else {
      if (currentMonth > 3) {
        if (semester === 'SoSe') {
          count += currentMonth < 10 ? 1 : 2
        } else {
          count += currentMonth < 10 ? 0 : 1
        }
      }
    }
    count = Math.min(count, 10)

    return count
  }

  private _migrate(): void {
    if (this.sessionVersion < 2) {
      // subject area keys have changed
      // for signed in users, this will be fixed via data-sync-session-data
      // guests will use the current subject area
      this.store.remove('subjectAreaIdAndKey')
      this.sessionVersion = 2
    }
    if (this.sessionVersion < 3) {
      // hasAdBlocker is no longer read from or written to the session; GA4 now learns it server-side
      this.store.remove('hasAdBlocker')
      this.sessionVersion = 3
    }
  }
}

class Store {
  private storage: ReturnType<typeof getSafeLocalStorage>
  private prefix: string

  constructor(prefix: string) {
    this.storage = getSafeLocalStorage()
    this.prefix = prefix
  }

  read(key: string): unknown {
    try {
      const prefixedKey = this.prefixedKey(key)
      const json = this.storage.getItem(prefixedKey)
      return JSON.parse(json as string)
    } catch (error) {
      return undefined
    }
  }

  write(key: string, value: unknown): void {
    const prefixedKey = this.prefixedKey(key)
    const json = JSON.stringify(value)
    this.storage.setItem(prefixedKey, json)
  }

  remove(key: string): void {
    this.storage.removeItem(`${this.prefix}.${key}`)
  }

  clear(): void {
    Object.keys(this.storage as Storage).forEach((key) => {
      if (key.indexOf(`${this.prefix}.`) === 0) {
        this.storage.removeItem(key)
      }
    })
  }

  private prefixedKey(key: string): string {
    return `${this.prefix}.${key}`
  }
}

const session = new Session()
export default session
